<template>
  <div class="company-info">
    <div class="row title">
      <div
        class="comp-menu"
        :class="{'comp-menu--half-width': !withDropdown}"
      >
        <DottedDropdown
          :id="'dir-comp-' + id"
          v-if="withDropdown"
        >
          <li>
            <DialogSlot
              :id="deleteModalId"
              :ref="deleteModalId"
              :modalBtnClass="'btn--transparent'"
              :modalBtn="$t('action.delete')"
            >
              <div
                slot="content"
                class="slot-content"
              >
                <span class="font-bold">{{ $t('deleteConfirmMsg') }}</span>
              </div>
              <div slot="buttons">
                <button
                  type="button"
                  class="btn btn--orange close-reveal-modal"
                  @click="deleteItem()"
                >{{ $t('action.yesContinue') }}</button>
              </div>
            </DialogSlot>
            <a
              class="dot-option-btn"
              v-if="index > 0"
              @click="moveUp"
            >{{ $t('directory.moveUp') }}</a>
            <a
              class="dot-option-btn"
              v-if="index < lengthOfArray - 1"
              @click="moveDown"
            >{{ $t('directory.moveDown') }}</a>
          </li>
        </DottedDropdown>
      </div>
      <label>{{ title }} {{ showIndex ? index + 1 : '' }}</label>
    </div>
    <div class="info">
      <InputField
        :label="labelEnName"
        :showFlag="true"
        :showColon="false"
        :value="detail.engName"
        :required="true"
        @updateValue="val => _updateField('engName', val)"
        :readonlyAsInput="!canCompInfoEditable"
      />

      <label class="emphasis">{{ labelChiName }}</label>
      <div class="row chinese-name">
        <label v-if="isEngineer">{{ $t('directory.label.traditionalChinese') }}</label>
        <InputField
          class="small-12 large-12"
          :showFlag="false"
          :showColon="false"
          :value="detail.chtName"
          @updateValue="updateChtAndClearChs"
          :readonlyAsInput="!canCompInfoEditable"
        />
      </div>
      <div
        class="row chinese-name"
        v-if="isSimplifiedChineseShow"
      >
        <label v-if="isEngineer">{{ $t('directory.label.simplifiedChinese') }}
          <i
            v-if="isPencilEdit && currentPencilEdit !== 'chsName'"
            class="fa fa-pencil fa-lg icon edit-field"
            @click="enablePencilEdit('chsName')"
          />
        </label>
        <button
          v-if="isPencilEdit && currentPencilEdit === 'chsName'"
          type="button"
          class="btn btn--orange save-btn-link"
          @click="saveDirectory"
        >{{ $t('action.save') }}</button>
        <button
          v-if="showTranslatorBtn"
          type="button"
          class="btn btn--darkblue translator-btn-link"
          @click="translateToSimpleChi(detail.chtName)"
          :disabled="disableTranslatorBtn && currentPencilEdit !== 'chsName'"
        >{{ $t('directory.hint.chinese') }}</button>
        <InputField
          class="small-12 large-12"
          :showFlag="false"
          :showColon="false"
          :value="detail.chsName"
          ref="chsName-input"
          @updateValue="val => _updateField('chsName', val)"
          :readonlyAsInput="!isSimplifiedChineseEditable && currentPencilEdit!=='chsName'"
        />
      </div>

      <InputField
        :label="$t('directory.label.floor') + ' ' + $t('directory.label.floorExample')"
        v-if="!(isFloorDR || isDetail)"
        :showFlag="false"
        :showColon="false"
        :value="detail.floor"
        :required="false"
        @updateValue="val => _updateField('floor', val)"
        :readonlyAsInput="!canCompInfoEditable"
      />

      <div
        class="row premise"
        v-if="isEngineer && !(isFloorDR || isDetail)"
      >
        <label>{{ $t('directory.label.premisesAtTerminal') }}
          <i
            v-if="isPencilEdit && currentPencilEdit !== 'premisesAtTerminal'"
            class="fa fa-pencil fa-lg icon edit-field"
            @click="enablePencilEdit('premisesAtTerminal')"
          />
        </label>
        <button
          v-if="isPencilEdit && currentPencilEdit === 'premisesAtTerminal'"
          type="button"
          class="btn btn--orange save-btn-link"
          @click="saveDirectory"
        >{{ $t('action.save') }}</button>
        <InputField
          class="small-12 large-12"
          :showFlag="false"
          :showColon="false"
          :value="detail.premisesAtTerminal"
          :required="false"
          ref="premisesAtTerminal-input"
          @updateValue="val => _updateField('premisesAtTerminal', val)"
          :readonlyAsInput="!canCompInfoEditable && currentPencilEdit!=='premisesAtTerminal'"
        />
      </div>
      <div
        class="row premise"
        v-if="isFloorDR && !isDetail"
      >
        <label>{{ $t('directory.label.premises') }}
          <i
            v-if="isPencilEdit && currentPencilEdit !== 'premises'"
            class="fa fa-pencil fa-lg icon edit-field"
            @click="enablePencilEdit('premises')"
          />
        </label>
        <button
          v-if="isPencilEdit && currentPencilEdit === 'premises'"
          type="button"
          class="btn btn--orange save-btn-link"
          @click="saveDirectory"
        >{{ $t('action.save') }}</button>
        <InputField
          class="small-12 large-12"
          :showFlag="isFloorDR && !isSub"
          :showColon="false"
          :value="detail.premises"
          :required="isFloorDR && !isSub"
          ref="premises-input"
          @updateValue="val => _updateField('premises', val)"
          :readonlyAsInput="!canCompInfoEditable && currentPencilEdit!=='premises'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapActions } from 'vuex'

import DialogSlot from '@/controls/DialogSlot'
import DottedDropdown from '@/controls/DottedDropdown'
import directoryCompanyMixin from './directoryCompanyMixin'
import InputField from '@/controls/InputField'
import i18n from '@/utils/i18n'
import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'

import { ActionTypes } from '@/utils/consts'
import { DIR_TYPE, DIR_ACTION, DIR_STATUS } from './consts'
import { utils as formUtils } from '@/plugin/InputChange'
import { translate } from '@/api/translator/request'

export default {
  name: 'DirectoryCompanyInfo',
  components: {
    DialogSlot,
    DottedDropdown,
    InputField,
  },
  mixins: [directoryCompanyMixin],
  props: {
    id: String,
    index: Number,
    isSub: Boolean,
    isDetail: Boolean,
    category: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      required: true,
    },
    detail: {
      type: Object,
      default: () => {},
    },
    resetValidation: {
      type: Function,
      default: () => {},
    },
    title: String,
    showIndex: {
      type: Boolean,
      default: true,
    },
    withDropdown: {
      type: Boolean,
      default: true,
    },
    lengthOfArray: {
      type: Number,
      default: 1,
    },
    canCompInfoEditable: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      currentPencilEdit: '',
    }
  },
  methods: {
    ...mapActions('directories', ['updateDirectory']),
    ...mapMutations('directories', {
      updateField: mutationTypes.DIRECTORY_SET_FIELD_BY_PATH,
      updateOrder: mutationTypes.DIRECTORY_SET_ORDER,
      removeCompany: mutationTypes.DIRECTORY_UPDATE_COMPANY,
    }),
    _updateField (field, value, isSave) {
      if (typeof value === 'string') {
        value = value.trim()
      }
      if (this.detail[field] !== value) {
        this.updateField({
          path: [...this.path, field],
          value,
        })
        if (isSave) {
          this.saveDirectory()
        }
      }
    },
    async saveDirectory () {
      await this.updateDirectory({ request: this.currentDirectory, headerAction: ActionTypes.directories.saved })
      formUtils.resetDirty()
      this.currentPencilEdit = ''
    },
    updateChtAndClearChs (val) {
      if (this.detail['chtName'] !== val) {
        this._updateField('chtName', val)
        this._updateField('chsName', '')
      }
    },
    async deleteItem () {
      await this.closeDeleteModal()
      this.removeCompany({
        path: this.category,
        index: this.index,
        action: 'REMOVE',
      })
      this.resetValidation()
      formUtils.setDirty()
    },
    closeDeleteModal () {
      this.$refs[this.deleteModalId].closeModal()
    },
    moveUp () {
      this.updateOrder({
        path: this.path,
        upward: true,
      })
    },
    moveDown () {
      this.updateOrder({
        path: this.path,
        upward: false,
      })
    },
    enablePencilEdit (field) {
      this.currentPencilEdit = field
      this.$nextTick(() => this.$refs[field + '-input'].$el.querySelector('input').focus())
    },
    async translateToSimpleChi (sourceText) {
      if (!sourceText || !sourceText.trim()) {
        notification.warn(i18n.t('directory.hint.emptyTraditionalChinese'))
        return
      }
      const result = await translate({ sourceText, target: 'zh-CN' })
      this._updateField('chsName', result)
    },
  },

  computed: {
    ...mapGetters('user', ['isEngineer', 'isTenant']),
    ...mapGetters('directories', ['currentDirectory', 'canCurrUserApprove', 'canCurrUserCreate']),
    path () {
      return [...this.category, this.index]
    },
    deleteModalId () {
      return `delete-comp-${this.id}-${Date.now()}`
    },
    isFloorDR () {
      return this.type === DIR_TYPE.FLOOR
    },
    isSimplifiedChineseShow () {
      return !this.isTenant
    },
    isSimplifiedChineseEditable () {
      return !this.isViewPage && this.currentDirectory.action !== DIR_ACTION.REMOVE && this.canCurrUserCreate && !this.isTenant
    },
    showTranslatorBtn () {
      return this.isEngineer && this.canCurrUserCreate && (this.canCompInfoEditable || (this.currentDirectory.action !== DIR_ACTION.REMOVE && !!this.detail.chtName))
    },
    isPencilEdit () {
      return this.isEngineer && this.isViewPage && this.canCurrUserCreate && this.currentDirectory.status === DIR_STATUS.PUBLISHED
    },
    labelEnName () {
      return this.isDetail || this.isSub ? this.$t('directory.label.engNameDetail') : this.$t('directory.label.engName')
    },
    labelChiName () {
      return this.isDetail || this.isSub ? this.$t('directory.label.chiNameDetail') : this.$t('directory.label.chiName')
    },
    disableTranslatorBtn () {
      return this.isViewPage
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/common';
@import '~styles/directories/common';

.company-info {
  background-color: #ffffff;

  .comp-menu {
    transform: translate(0, 0%);
    z-index: 1000;
    padding: 8px 0;
  }

  .title {
    border-bottom: #0C3A58 1px solid;
  }

  .buttons {
    padding: 15px 0;
    text-align: center
  }

  .chinese-name .columns {
    padding-right: 10px;
  }
  .translator-btn-link, .save-btn-link{
    margin-left: 20px;
    margin-bottom: 5px;
    line-height: 12px;
    padding: 2px 5px;
  }
  .premise .save-btn-link{
    margin-top: 15px;
  }
  .edit-field{
    color: #3879b8;
    cursor: pointer;
  }
}
</style>

<style lang="scss">
.company-info {
  .input-field input {
    width: 100%!important;
  }
  .chinese-name label, .chinese-name .input-field label {
    margin-top: 0;
    font-size: 12px;
    font-weight: normal;
    color: var(--twilight-blue);
  }

}

.main-comp .comp-menu .tender-ellipsis-size {
  color: #FFF;
}

.comp-menu {
  width: 26px;
  height: 51px;
  .dot-option-btn {
    height: 38px;
  }
}
.comp-menu--half-width {
  width: 13px
}
</style>
